import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type NgCalendarProps = {
  value?: Date | null;
  onValueChanged?: (value: Date) => void | Promise<void>;
  minDate?: Date;
  maxDate?: Date;
};

export const CALENDAR_DEFAULT_MIN_DATE = new Date(1900, 0, 1);
export const CALENDAR_DEFAULT_MAX_DATE = new Date(2099, 11, 31);

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function firstOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
  );
}

function firstDayOfWeekFromEnglishConvention(locale: string): number {
  return locale.toLowerCase().startsWith('en') ? 0 : 1;
}

function firstDayOfWeek(locale: string): number {
  try {
    const intlLocale = new Intl.Locale(locale) as Intl.Locale & {
      getWeekInfo?: () => { firstDay: number };
      weekInfo?: { firstDay: number };
    };
    const weekInfo = intlLocale.getWeekInfo?.() ?? intlLocale.weekInfo;
    if (weekInfo) {
      return weekInfo.firstDay % 7;
    }
  } catch {
    return firstDayOfWeekFromEnglishConvention(locale);
  }
  return firstDayOfWeekFromEnglishConvention(locale);
}

export function NgCalendar({ value, onValueChanged, minDate, maxDate }: NgCalendarProps) {
  const { i18n } = useTranslation();
  const locale = i18n.language || 'en-US';

  const minDateEffective = startOfDay(minDate ?? CALENDAR_DEFAULT_MIN_DATE);
  const maxDateEffective = startOfDay(maxDate ?? CALENDAR_DEFAULT_MAX_DATE);
  const valueDay = value != null ? startOfDay(value) : null;

  const clampDisplayMonth = (month: Date): Date => {
    if (month < firstOfMonth(minDateEffective)) {
      return firstOfMonth(minDateEffective);
    }
    if (month > firstOfMonth(maxDateEffective)) {
      return firstOfMonth(maxDateEffective);
    }
    return month;
  };

  const [displayMonth, setDisplayMonth] = useState<Date>(() =>
    clampDisplayMonth(firstOfMonth(valueDay ?? new Date())),
  );

  const lastKnownValueRef = useRef<number | null>(valueDay?.getTime() ?? null);
  useEffect(() => {
    const current = valueDay?.getTime() ?? null;
    if (lastKnownValueRef.current !== current) {
      lastKnownValueRef.current = current;
      setDisplayMonth(
        valueDay != null
          ? firstOfMonth(valueDay)
          : clampDisplayMonth(firstOfMonth(new Date())),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueDay?.getTime()]);

  const monthNames = useMemo(() => {
    const format = new Intl.DateTimeFormat(locale, { month: 'long' });
    return Array.from({ length: 12 }, (_, month) => format.format(new Date(2000, month, 1)));
  }, [locale]);

  const weekDayNames = useMemo(() => {
    const format = new Intl.DateTimeFormat(locale, { weekday: 'short' });
    const firstDay = firstDayOfWeek(locale);
    return Array.from({ length: 7 }, (_, i) =>
      format.format(new Date(2023, 0, 1 + ((firstDay + i) % 7))),
    );
  }, [locale]);

  const years = useMemo(() => {
    const yearSet = new Set<number>();
    for (let year = minDateEffective.getFullYear(); year <= maxDateEffective.getFullYear(); year++) {
      yearSet.add(year);
    }
    yearSet.add(displayMonth.getFullYear());
    return [...yearSet].sort((a, b) => b - a);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minDateEffective.getTime(), maxDateEffective.getTime(), displayMonth]);

  const daysInMonth = new Date(
    displayMonth.getFullYear(),
    displayMonth.getMonth() + 1,
    0,
  ).getDate();

  let firstDayToDisplay = addDays(
    displayMonth,
    -1 * (((displayMonth.getDay() - firstDayOfWeek(locale)) % 7 + 7) % 7),
  );
  if (daysInMonth === 28 && displayMonth.getDay() === firstDayOfWeek(locale)) {
    firstDayToDisplay = addDays(firstDayToDisplay, -7);
  }

  const today = startOfDay(new Date());
  const weeks: { key: number; days: Date[] }[] = [];
  let currentDay = firstDayToDisplay;
  for (let week = 0; week < 6; week++) {
    const days: Date[] = [];
    for (let day = 0; day < 7; day++) {
      days.push(currentDay);
      currentDay = addDays(currentDay, 1);
    }
    weeks.push({ key: week, days });
  }

  const previousMonthEnabled = minDateEffective < displayMonth;
  const nextMonthEnabled =
    maxDateEffective > new Date(displayMonth.getFullYear(), displayMonth.getMonth(), daysInMonth);

  const monthEnabled = (monthIndex: number): boolean => {
    const year = displayMonth.getFullYear();
    const lastDay = new Date(year, monthIndex + 1, 0);
    const firstDay = new Date(year, monthIndex, 1);
    return lastDay >= minDateEffective && firstDay <= maxDateEffective;
  };

  const handlePreviousMonthClick = () => {
    setDisplayMonth(new Date(displayMonth.getFullYear(), displayMonth.getMonth() - 1, 1));
  };

  const handleNextMonthClick = () => {
    setDisplayMonth(new Date(displayMonth.getFullYear(), displayMonth.getMonth() + 1, 1));
  };

  const handleDayClick = (day: Date) => {
    void onValueChanged?.(day);
  };

  return (
    <div className="hx-calendar">
      <div className="hx-calendar-navigation">
        <button
          type="button"
          className="hx-button btn  btn-sm hx-calendar-navigation-button"
          disabled={!previousMonthEnabled}
          onClick={(event) => {
            event.stopPropagation();
            handlePreviousMonthClick();
          }}
          tabIndex={-1}
        >
          <i className="hx-icon bi-chevron-left"></i>
        </button>
        <div className="hx-calendar-month-year-select">
          <select
            className="form-select form-select-sm"
            aria-label="Month"
            tabIndex={-1}
            value={displayMonth.getMonth()}
            onChange={(event) =>
              setDisplayMonth(new Date(displayMonth.getFullYear(), Number(event.target.value), 1))
            }
            onClick={(event) => event.stopPropagation()}
          >
            {monthNames.map((name, index) => (
              <option key={index} value={index} disabled={!monthEnabled(index)}>
                {name}
              </option>
            ))}
          </select>
          <select
            className="form-select form-select-sm"
            aria-label="Year"
            tabIndex={-1}
            value={displayMonth.getFullYear()}
            onChange={(event) =>
              setDisplayMonth(new Date(Number(event.target.value), displayMonth.getMonth(), 1))
            }
            onClick={(event) => event.stopPropagation()}
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          className="hx-button btn  btn-sm hx-calendar-navigation-button"
          disabled={!nextMonthEnabled}
          onClick={(event) => {
            event.stopPropagation();
            handleNextMonthClick();
          }}
          tabIndex={-1}
        >
          <i className="hx-icon bi-chevron-right"></i>
        </button>
      </div>

      <div>
        <div className="hx-calendar-day-names">
          {weekDayNames.map((dayOfWeek) => (
            <div key={dayOfWeek}>{dayOfWeek}</div>
          ))}
        </div>
        {weeks.map((week) => (
          <div key={week.key} className="hx-calendar-week">
            {week.days.map((day) => {
              const clickEnabled = day >= minDateEffective && day <= maxDateEffective;
              const cssClass = [
                clickEnabled ? 'hx-calendar-day-active' : 'hx-calendar-day-disabled',
                valueDay != null && sameDay(day, valueDay) ? 'hx-calendar-day-selected' : null,
                day.getMonth() === displayMonth.getMonth() &&
                day.getFullYear() === displayMonth.getFullYear()
                  ? 'hx-calendar-day-in'
                  : 'hx-calendar-day-out',
                sameDay(day, today) ? 'hx-calendar-day-today' : null,
                day.getDay() === 6 || day.getDay() === 0 ? 'hx-calendar-day-weekend' : null,
              ]
                .filter((cls) => cls != null)
                .join(' ');

              return clickEnabled ? (
                <button
                  key={day.getDate()}
                  type="button"
                  className={`btn hx-calendar-day ${cssClass}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleDayClick(day);
                  }}
                  tabIndex={-1}
                >
                  {day.getDate()}
                </button>
              ) : (
                <div key={day.getDate()} className={`hx-calendar-day ${cssClass}`}>
                  {day.getDate()}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
